






import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { Bar } from 'vue-chartjs/legacy';
import { ChartData, ChartOptions } from 'chart.js';
import { CreateSurveyElement } from '@/models/Presentation';

@Component({
  components: { Bar }
})
export default class HorizontalBarChart extends Vue {

  @Prop() surveyElement!: CreateSurveyElement;
  @Prop() values!: number[];
  @Prop() totalData!: number;
  @Prop({ default: false }) large!: boolean;

  chartKey = 0;
  animationDuration = 1000;
  containerSize = { width: 0, height: 0 };

  isUpdatedAfterAnimation = false;

  mounted() {
    this.isUpdatedAfterAnimation = false;
    const container = this.$refs.container as HTMLElement;
    if (!container) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        this.containerSize.width = entry.contentRect.width;
        this.containerSize.height = entry.contentRect.height;
      }
    });

    resizeObserver.observe(container);
  }

  get labelFontSize(): number {
    // Space per answer in px
    const spacePerItem = this.containerSize.height / this.surveyElement.answers.length;
    // Calculate the font size based on the available space and the longest answer length
    const fontSize = Math.max(Math.min(Math.floor(spacePerItem * 0.8), 16), 10); // Limit font size between 10 and 20px

    console.log(`Calculated font size: ${fontSize}px`);
    return fontSize;
  }

  get paddingRight(): number {
    console.log(`Container width: ${this.containerSize.width * 0.4}px`);
    return this.containerSize.width * 0.4;
  }

  get chartData(): ChartData {
    return {
      labels: this.surveyElement.answers.map((answer, i) =>
        (this.surveyElement.chartNumberType == 'PERCENT'
          ? `${(this.values[i] / this.totalData * 100).toFixed(1)}%`
          : this.values[i]))
      ,
      datasets: [
        {
          label: '',
          data: this.surveyElement.chartNumberType === 'PERCENT'
            ? this.values.map(value => (value / this.totalData) * 100)
            : this.values,
          backgroundColor: this.surveyElement.answers.map(answer => answer.color)
        }
      ]
    };
  }

  @Watch('surveyElement.answers', { deep: true })
  onSurveyElementChange() {
    // Set the animation duration to 0 to prevent the chart from animating when the data changes
    this.animationDuration = 0;
    this.chartKey++;
    this.$nextTick(
      () => this.animationDuration = 1000
    );
  }

  get chartOptions(): ChartOptions {
    return {
      responsive: true,
      maintainAspectRatio: false,
      indexAxis: 'y',
      animation: {
        duration: this.animationDuration,
        onComplete: (event) => {
          if (!this.isUpdatedAfterAnimation) {
            this.isUpdatedAfterAnimation = true;
            event.chart.update();
          }
        }
      },
      layout: {
        padding: {
          right: this.paddingRight
        }
      },
      plugins: {
        legend: {
          display: false
        },
        tooltip: {
          enabled: true,
          displayColors: false,
          filter: (tooltipItem) => {
            const index = tooltipItem.dataIndex;
            const text = this.surveyElement.answers[index].text;
            return !!text && text.trim().length > 0;
          },
          callbacks: {
            title: (tooltipItems) => {
              if(!tooltipItems[0]){
                return '';
              }
              const index = tooltipItems[0].dataIndex;
              const fullText = this.surveyElement.answers[index].text;

              // Wenn du automatische Zeilenumbrüche willst, splitte manuell nach Länge
              const words = fullText.split(' ');
              const lines = [];
              let line = '';
              words.forEach(word => {
                if ((line + word).length > 25) {
                  lines.push(line);
                  line = word + ' ';
                } else {
                  line += word + ' ';
                }
              });
              lines.push(line.trim());
              return lines; // Tooltip wird mehrzeilig
            },
            label: () => '', // Verstecke Wert
          },
          yAlign: 'center',
          xAlign: 'left',
          caretSize: 0,
          padding: 10,
          titleFont: {
            size: 12,
          },
          bodyFont: {
            size: 0, // Kein Bodytext
          },
        },
        datalabels: {
          anchor: 'end',
          align: 'right',
          formatter: (value: number, ctx) => {
            const chart = ctx.chart;
            const meta = chart.getDatasetMeta(ctx.datasetIndex);
            const barElement = meta.data[ctx.dataIndex];
            const correct = this.surveyElement.answers[ctx.dataIndex].correct;
            if (!barElement) return this.surveyElement.answers[ctx.dataIndex].text  + (correct ? ' ✅' : '');

            const barRightEdge = barElement.x;
            const chartWidth = chart.chartArea.right;
            const remainingSpace = chartWidth - barRightEdge + this.paddingRight - (this.labelFontSize * 2); // 20px padding

            const text = this.surveyElement.answers[ctx.dataIndex].text;
            const ctxFontSize = this.labelFontSize || 12;
            const ctxFont = `${ctxFontSize}px Arial`;
            const context = ctx.chart.ctx;
            context.font = ctxFont;

            // Wenn der gesamte Text passt, direkt anzeigen
            if (context.measureText(text).width <= remainingSpace) {
              return text  + (correct ? ' ✅' : '');
            }

            const spacePerItem = this.containerSize.height / this.surveyElement.answers.length;
            // Wenn es mehr als 4 Antworten gibt, dann kürzen
            if (spacePerItem < this.labelFontSize * 2.5) {
              // Ansonsten schrittweise kürzen und mit '...' versehen
              let truncatedText = text;
              while (truncatedText.length > 1 && context.measureText(truncatedText + '...').width > remainingSpace - (correct ? this.labelFontSize * 1.5 : 0)) {
                truncatedText = truncatedText.slice(0, -1);
              }
              return truncatedText + '...'  + (correct ? ' ✅' : '     ');
            }

            // Sonst zeigen wir den Text in mehreren Zeilen an
            const words = text.split(' ');
            let firstLine = '';
            let secondLine = '';

            // Fülle die erste Zeile so lange mit Wörtern, bis sie zu lang wird
            for (const word of words) {
              const testLine = firstLine + word + ' ';
              if (context.measureText(testLine).width > remainingSpace) {
                break;
              }
              firstLine = testLine;
            }

            // Den restlichen Text in die zweite Zeile packen
            secondLine = words.slice(firstLine.trim().split(' ').length).join(' ');

            // Wenn die zweite Zeile zu lang ist, kürzen und '...' hinzufügen
            while (secondLine.length > 1 && context.measureText(secondLine + '...' + (correct ? '✅' : '')).width > remainingSpace) {
              secondLine = secondLine.slice(0, -1);
            }

            if (secondLine.length < words.slice(firstLine.trim().split(' ').length).join(' ').length) {
              secondLine += '...' + (correct ? ' ✅' : '');
            }

            return [firstLine.trim(), secondLine.trim()];

          },
          color: '#000',
          font: {
            size: this.labelFontSize
          }
        }
      },
      scales: {
        x: {
          grid: { display: false, drawBorder: false },
          ticks: { display: false },
        },
        y: {
          grid: { display: false, drawBorder: false },
          ticks: {
            color: '#000',
            display: true,
            align: 'center',
            font: {
              size: this.large ? 16 : 12,
            }
          }
        }
      }
    };
  }
}
